import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import {
  connectionOrEmpty,
  type FqsRequestOptions,
  fqsRequest,
  fqsRequestAll,
  type GraphqlPageInfo,
  MAX_OBJECTS_PER_REQUEST,
} from '@ctw/shared/api/fqs/client';
import {
  type DiagnosticReportGraphqlResponse,
  getDiagnosticReportCursorQuery,
  getDiagnosticReportOverviewQuery,
  getDiagnosticReportQuery,
} from '@ctw/shared/api/fqs/queries/diagnostic-reports-query';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { DiagnosticReportSortParams } from '@ctw/shared/types/graphql/__generated__/graphql';
import {
  QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS,
  QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS_OVERVIEW,
  QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS_PAGE,
} from '@ctw/shared/utils/query-keys';
import type { GraphQLClient } from 'graphql-request';
import { useMemo } from 'react';

// Gets diagnostic reports for the patient. Uses a lightweight GraphQL fragment
// that omits the Practitioner and Patient/Organization resources referenced via
// `performer.resource` and `subject.resource`. Those are only needed by the
// drawer — the drawer calls {@link useDiagnosticReportById} to refetch with the
// full fragment when it opens.
export function usePatientDiagnosticReports(limit = MAX_OBJECTS_PER_REQUEST, enabled = true) {
  const { requestContext } = useCTW();
  const { builderId } = requestContext;

  const options = useMemo(
    () => ({
      staleTime: 30_000,
      queryId: QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS_OVERVIEW,
      queryKey: [limit], // Only use the IDs in our key (fixes issue with circular references).
      queryFn: async ({
        graphqlClient,
        telemetry,
        patient,
      }: {
        graphqlClient: GraphQLClient;
        telemetry: ReturnType<typeof useTelemetry>;
        patient: PatientModel;
      }) => {
        const data = await fetchDiagnosticReportsOverviewFromFQS(
          telemetry,
          graphqlClient,
          patient,
          limit,
          {
            requestHeaders: {
              'X-Use-Hotstorage': '1',
            },
          },
        );

        // Precompute the sort flag and dedupe key for each report once. The
        // previous implementation used `uniqWith(..., isEqual)` which is O(n²)
        // and reinvoked the `displayName` / `dateDisplay` / `ownedByBuilder`
        // getters millions of times for large result sets — blocking the main
        // thread and keeping `isLoading` true after the network was done.
        const enrichedReports = data.DiagnosticReportConnection.edges.map((x) => {
          const report = new DiagnosticReportModel(x.node, undefined, x.node.BasicList);
          return {
            report,
            isOwned: report.ownedByBuilder(builderId),
            dedupeKey: `${report.displayName}\u0000${report.dateDisplay ?? ''}`,
          };
        });

        // Stable-sort (Array.prototype.sort is stable in all modern engines)
        // with builder-owned reports first.
        enrichedReports.sort((a, b) => {
          if (a.isOwned === b.isOwned) {
            return 0;
          }
          return a.isOwned ? -1 : 1;
        });

        // Dedupe via Set in O(n), preserving first occurrence (builder-owned wins).
        const seen = new Set<string>();
        const deduped: Array<DiagnosticReportModel> = [];
        for (const { report, dedupeKey } of enrichedReports) {
          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            deduped.push(report);
          }
        }
        return deduped;
      },
      enabled,
    }),
    [builderId, limit, enabled],
  );

  return usePatientQuery(options);
}

/**
 * Fetches a single diagnostic report by ID with the full GraphQL fragment
 * (including performer.resource and subject.resource). Pair with
 * {@link usePatientDiagnosticReports}: the list uses the lite query for fast
 * paginated rendering; the drawer calls this hook to load the heavier
 * per-record data on demand.
 */
export function useDiagnosticReportById(id: string | undefined) {
  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS,
    queryKey: [id],
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      if (!id) {
        return undefined;
      }
      // Delegate to `fetchDiagnosticReportsFromFQSById` so we share its
      // `fqsRequestAll` dedupe key with the drawer-context's deep-link fetch
      // (which also calls this function). Without shared key, the drawer would
      // fire two separate network calls for the same DR on open.
      const models = await fetchDiagnosticReportsFromFQSById(
        telemetry,
        graphqlClient,
        patient,
        [id],
        {
          requestHeaders: {
            'X-Use-Hotstorage': '1',
          },
        },
      );
      return models[0];
    },
    enabled: !!id,
  });
}

async function fetchDiagnosticReportsOverviewFromFQS(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  limit: number,
  options?: FqsRequestOptions,
) {
  const { data } = await fqsRequestAll<DiagnosticReportGraphqlResponse>(
    telemetry,
    graphqlClient,
    getDiagnosticReportOverviewQuery,
    'DiagnosticReportConnection',
    {
      upid: patient.UPID,
      cursor: 'DiagnosticReportConnection',
      filter: {},
      first: limit,
    },
    undefined,
    options,
  );
  return data;
}

export async function fetchDiagnosticReportsFromFQSById(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  ids: Array<string> = [],
  options?: FqsRequestOptions,
) {
  const { data } = await fqsRequestAll<DiagnosticReportGraphqlResponse>(
    telemetry,
    graphqlClient,
    getDiagnosticReportQuery,
    'DiagnosticReportConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: 500,
      // Matches `useDiagnosticReportById` so both call sites produce the same
      // `fqsRequestAll` dedupe key and the drawer-open flow only fires one network
      // request (when the drawer body and the drawer-context's deep-link useEffect
      // both try to refetch the same DR by id).
      filter: {
        ids: { anymatch: ids },
      },
    },
    undefined,
    options,
  );
  return data.DiagnosticReportConnection.edges.map(
    (x) => new DiagnosticReportModel(x.node, undefined, x.node.BasicList),
  );
}

export interface DiagnosticReportsPage {
  reports: Array<DiagnosticReportModel>;
  pageInfo: GraphqlPageInfo;
}

// Fetches a single cursor page of diagnostic reports for the server-driven diagnostics path
// (ctw-diagnostics-resource-view-controls-server-side). Uses the non-paginating `fqsRequest` so
// FQS can order the rows by `sort`; `fqsRequestAll` throws on a non-empty sort by design.
export async function fetchDiagnosticReportsOverviewPageFromFQS(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  variables: { cursor: string; first: number; sort: DiagnosticReportSortParams },
  options?: FqsRequestOptions,
): Promise<DiagnosticReportsPage> {
  const { data } = await fqsRequest<DiagnosticReportGraphqlResponse>(
    telemetry,
    graphqlClient,
    getDiagnosticReportOverviewQuery,
    {
      upid: patient.UPID,
      cursor: variables.cursor,
      first: variables.first,
      sort: variables.sort,
      filter: {},
    },
    options,
  );

  const { edges, pageInfo } = connectionOrEmpty(data.DiagnosticReportConnection);
  return {
    // No client dedupe here (unlike usePatientDiagnosticReports): FQS orders and pages the rows,
    // so cross-source duplicates render as adjacent rows on the server-driven path.
    reports: edges.map((x) => new DiagnosticReportModel(x.node, undefined, x.node.BasicList)),
    pageInfo,
  };
}

export interface DiagnosticReportsCursorAdvance {
  // Cursors discovered while skipping forward, keyed by the 0-based page index each one seeds
  // (cursorsByPageIndex[k] fetches page k + 1). Only whole-page boundaries are recorded; every
  // chunk boundary is one because the MAX_OBJECTS_PER_REQUEST chunk size and the leftover `count`
  // are both multiples of `pageSize`.
  cursorsByPageIndex: Record<number, string>;
  // True when FQS reported no further pages before the full `count` was skipped (e.g. the
  // connection shrank because a hotload finished mid-jump).
  exhausted: boolean;
}

// Skips `count` records forward from `cursor` using requests that carry no resource bodies (just
// pageInfo and a minimal id-only edge list, to confirm each chunk's actual size), so a
// numbered-page jump to an unseen page costs one request per MAX_OBJECTS_PER_REQUEST records
// skipped rather than one per page. Records a page-aligned cursor at every chunk boundary for the
// caller's cursor map. `X-Use-Hotstorage` must be set in `options`: FQS serves the date-column
// sort only from hotstorage.
export async function advanceDiagnosticReportsCursor(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  variables: {
    cursor: string;
    fromPageIndex: number;
    pageSize: number;
    count: number;
    sort: DiagnosticReportSortParams;
  },
  options?: FqsRequestOptions,
): Promise<DiagnosticReportsCursorAdvance> {
  const { cursor, fromPageIndex, pageSize, count, sort } = variables;
  if (pageSize <= 0) {
    throw new Error(
      `advanceDiagnosticReportsCursor: pageSize must be greater than 0, got ${pageSize}`,
    );
  }

  const cursorsByPageIndex: Record<number, string> = {};
  let currentCursor = cursor;
  let recordsSkipped = 0;

  while (recordsSkipped < count) {
    const first = Math.min(count - recordsSkipped, MAX_OBJECTS_PER_REQUEST);
    const { data } = await fqsRequest<DiagnosticReportGraphqlResponse>(
      telemetry,
      graphqlClient,
      getDiagnosticReportCursorQuery,
      {
        upid: patient.UPID,
        cursor: currentCursor,
        first,
        sort,
        filter: {},
      },
      options,
    );
    const { pageInfo, edges } = connectionOrEmpty(data.DiagnosticReportConnection);

    if (edges.length !== first) {
      // FQS filters before slicing on every serving path (a "peek `first + 1`" query, filtered
      // then limited in one step), so a page shorter than requested always means the connection
      // ran out — never a full page trimmed short after the fact. `hasNextPage` should agree;
      // if it doesn't, that invariant no longer holds and continuing would silently fabricate a
      // boundary, so fail loudly instead.
      if (pageInfo.hasNextPage) {
        throw new Error(
          `advanceDiagnosticReportsCursor: got a short page (${edges.length} of ${first} requested) with hasNextPage: true`,
        );
      }
      // Ran out of records before reaching `count`: stop before recording a partial boundary so
      // the caller clamps to the last full page it did reach.
      return { cursorsByPageIndex, exhausted: true };
    }

    recordsSkipped += first;
    currentCursor = pageInfo.endCursor ?? currentCursor;
    cursorsByPageIndex[fromPageIndex + recordsSkipped / pageSize] = currentCursor;
  }

  return { cursorsByPageIndex, exhausted: false };
}

// Server-driven variant of usePatientDiagnosticReports for the
// ctw-diagnostics-resource-view-controls-server-side flag: FQS orders and paginates, so no client
// dedupe runs — cross-source duplicates render as adjacent rows.
export function usePatientDiagnosticReportsPage(
  pageSize: number,
  cursor: string,
  sort: DiagnosticReportSortParams,
) {
  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS_PAGE,
    // Keyed on every sort field so cache entries don't collide across sort selections.
    queryKey: [pageSize, cursor, sort.date ?? null, sort.lastUpdated ?? null, sort.name ?? null],
    staleTime: 30_000,
    queryFn: ({ graphqlClient, telemetry, patient }) =>
      fetchDiagnosticReportsOverviewPageFromFQS(
        telemetry,
        graphqlClient,
        patient,
        { cursor, first: pageSize, sort },
        {
          requestHeaders: {
            'X-Use-Hotstorage': '1',
          },
        },
      ),
  });
}
